import {Fragment} from 'react';
import {Popover, Transition} from '@headlessui/react';
import {MenuIcon, XIcon} from '@heroicons/react/outline';
import {Link} from "react-router-dom";
import {
    DOCTOR_DASHBOARD_HOME_LINK,
    DOCTOR_ROLE_TEXT,
    LABO_DASHBOARD_HOME_LINK,
    PHARMACY_DASHBOARD_HOME_LINK,
    PHARMACY_ROLE_TEXT,
    PUBLIC_HOME_LINK,
    PUBLIC_LOGIN_LINK,
    PUBLIC_SIGNUP_LINK
} from "../../../../shared/utils/const";
import {useAppStore} from "../../../../shared/appStore/useAppStore";
import {useTranslation} from "react-i18next";
import LanguageSwitcher from "../../../../shared/components/language-switcher/LanguageSwitcher";


const navigation = [
    {name: 'home.menu.benefits', href: '#features'},
    {name: 'home.menu.about', href: '#about'},
    {name: 'home.menu.contact-us', href: '#contact-us'},
];

export default function HeroSection() {
    const {isAuthenticated, role} = useAppStore();
    const {t, i18n} = useTranslation();

    const loginText = t('home.menu.login');
    const registerText = t('home.menu.sign-up');

    return (
        <div className="relative bg-white overflow-hidden">
            <div className='absolute top-6 right-6 z-10'>
                <LanguageSwitcher />
            </div>
            <div className="max-w-7xl mx-auto">
                <div className={`relative z-10 pb-8 bg-white sm:pb-16 md:pb-20 ${i18n.language === 'en' ? 'lg:max-w-2xl' : 'lg:max-w-4xl'} lg:w-full lg:pb-28 xl:pb-32`}>
                    <svg
                        className="hidden lg:block absolute right-0 inset-y-0 h-full w-48 text-white transform translate-x-1/2"
                        fill="currentColor"
                        viewBox="0 0 100 100"
                        preserveAspectRatio="none"
                        aria-hidden="true"
                    >
                        <polygon points="50,0 100,0 50,100 0,100"/>
                    </svg>

                    <Popover>
                        {({open}) => (
                            <>
                                <div className="relative pt-6 px-4 sm:px-6 lg:px-8">
                                    <nav
                                        className=" relative flex items-center justify-between sm:h-10 lg:justify-start"
                                        aria-label="Global"
                                    >
                                        <div className="flex items-center flex-grow flex-shrink-0 lg:flex-grow-0">
                                            <div className="flex items-center justify-between w-full md:w-auto">
                                                <Link to={PUBLIC_HOME_LINK}>
                                                    <span className="sr-only">Avicenne</span>
                                                    <img
                                                        className="h-8 w-auto sm:h-10"
                                                        src="/logo-name.png"
                                                        alt="logo"
                                                    />
                                                </Link>
                                                <div className="-mr-2 flex items-center md:hidden">
                                                    <Popover.Button
                                                        className="bg-white rounded-md p-2 inline-flex items-center justify-center text-gray-400 hover:text-gray-500 hover:bg-gray-100 focus:outline-none focus:ring-2 focus:ring-inset focus:ring-indigo-500">
                                                        <span className="sr-only">Open main menu</span>
                                                        <MenuIcon className="h-6 w-6" aria-hidden="true"/>
                                                    </Popover.Button>
                                                </div>
                                            </div>
                                        </div>

                                        <div className="hidden md:block md:ml-10 md:space-x-8">
                                            {navigation.map((item) => (
                                                <a key={item.href} href={item.href}
                                                   className="font-small text-gray-500 hover:text-gray-900">
                                                    {t(item.name)}
                                                </a>
                                            ))}
                                            {
                                                isAuthenticated ?
                                                    <Link
                                                        className="font-medium text-indigo-600 bg-cyan-100 p-3 rounded hover:text-indigo-500"
                                                        to={role === DOCTOR_ROLE_TEXT ? DOCTOR_DASHBOARD_HOME_LINK : role === PHARMACY_ROLE_TEXT ? PHARMACY_DASHBOARD_HOME_LINK : LABO_DASHBOARD_HOME_LINK}>
                                                        {t('home.menu.dashboard')}
                                                    </Link>
                                                    :
                                                    <>
                                                        <Link to={PUBLIC_LOGIN_LINK}
                                                              className="font-medium text-indigo-600 hover:text-indigo-500">
                                                            {loginText}
                                                        </Link>
                                                        <Link to={PUBLIC_SIGNUP_LINK}
                                                              className="font-medium text-indigo-500 hover:text-indigo-600">
                                                            {registerText}
                                                        </Link>
                                                    </>
                                            }
                                        </div>

                                    </nav>
                                </div>

                                <Transition
                                    show={open}
                                    as={Fragment}
                                    enter="duration-150 ease-out"
                                    enterFrom="opacity-0 scale-95"
                                    enterTo="opacity-100 scale-100"
                                    leave="duration-100 ease-in"
                                    leaveFrom="opacity-100 scale-100"
                                    leaveTo="opacity-0 scale-95"
                                >
                                    <Popover.Panel
                                        focus
                                        static
                                        className="absolute top-0 inset-x-0 p-2 transition transform origin-top-right md:hidden"
                                    >
                                        <div
                                            className="rounded-lg shadow-md bg-white ring-1 ring-black ring-opacity-5 overflow-hidden">
                                            <div className="px-5 pt-4 flex items-center justify-between">
                                                <div>
                                                    <img
                                                        className="h-8 w-auto"
                                                        src="./logo-name.png"
                                                        alt=""
                                                    />
                                                </div>
                                                <div className="-mr-2">
                                                    <Popover.Button
                                                        className="bg-white rounded-md p-2 inline-flex items-center justify-center text-gray-400 hover:text-gray-500 hover:bg-gray-100 focus:outline-none focus:ring-2 focus:ring-inset focus:ring-indigo-500">
                                                        <span className="sr-only">Close main menu</span>
                                                        <XIcon className="h-6 w-6" aria-hidden="true"/>
                                                    </Popover.Button>
                                                </div>
                                            </div>
                                            <div className="px-2 pt-2 pb-3 space-y-1">
                                                {navigation.map((item) => (
                                                    <a
                                                        key={item.name}
                                                        href={item.href}
                                                        className="block px-3 py-2 rounded-md text-base font-medium text-gray-700 hover:text-gray-900 hover:bg-gray-50"
                                                    >
                                                        {t(item.name)}
                                                    </a>
                                                ))}
                                                <a href={PUBLIC_LOGIN_LINK}
                                                   className="block px-3 py-2 rounded-md text-base font-medium text-indigo-600 hover:text-indigo-500">
                                                    {loginText}
                                                </a>
                                                <a href={PUBLIC_SIGNUP_LINK}
                                                   className=" block px-3 py-2 rounded-md text-base font-medium text-indigo-600 hover:text-indigo-500">
                                                    {registerText}
                                                </a>
                                            </div>

                                        </div>
                                    </Popover.Panel>
                                </Transition>
                            </>
                        )}
                    </Popover>

                    <main className="mt-10 mx-auto max-w-7xl px-4 sm:mt-12 sm:px-6 md:mt-16 lg:mt-20 lg:px-8 xl:mt-28">
                        <div className="sm:text-center lg:text-left">
                            <h1 className="text-4xl tracking-tight font-extrabold text-gray-900 sm:text-5xl md:text-6xl">
                                <span className="block xl:inline">{t('home.hero.title')}</span>{' '}
                                <span className="block text-indigo-600 xl:inline">{t('home.hero.sub-title')}</span>
                            </h1>
                            <p className="mt-3 text-base text-gray-500 sm:mt-5 sm:text-lg sm:max-w-xl sm:mx-auto md:mt-5 md:text-xl lg:mx-0">
                                {t('home.hero.description')}
                            </p>
                            <div className="mt-5 sm:mt-8 sm:flex sm:justify-center lg:justify-start">
                                <div className="rounded-md shadow">
                                    <button
                                        type="button"
                                        className="w-full flex items-center justify-center px-8 py-3 border border-transparent text-base font-medium rounded-md text-white bg-indigo-600 hover:bg-indigo-700 md:py-4 md:text-lg md:px-10"
                                    >
                                        {t('home.hero.buttons.discover-us')}
                                    </button>
                                </div>
                                <div className="mt-3 sm:mt-0 sm:ml-3">
                                    <button
                                        type="button"
                                        className="w-full flex items-center justify-center px-8 py-3 border border-transparent text-base font-medium rounded-md text-indigo-700 bg-indigo-100 hover:bg-indigo-200 md:py-4 md:text-lg md:px-10"
                                    >
                                        {t('home.hero.buttons.request-demo')}
                                    </button>
                                </div>
                            </div>
                        </div>
                    </main>
                </div>
            </div>
            <div className={`lg:absolute lg:inset-y-0 lg:right-0 ${i18n.language === 'fr' ? 'lg:w-1/3' : 'lg:w-1/2' }`}>
                <img
                    className="h-56 w-full object-cover sm:h-72 md:h-96 lg:w-full lg:h-full"
                    src="./images/hero-image.jpg"
                    alt=""
                />
            </div>
        </div>
    )
}
